import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tooltip,
  Tab,
  Tabs,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { useState } from "react";
import CloseButton from "../buttons/CloseButton";
import { DefaultPrimaryButton } from "@/styles/globalstyles";
import type {
  ParsedImportV1Result,
  ImportedChildPartV1,
  ImportWarning,
} from "@/utils/excel";

type PreviewTab = "info" | "material-cost" | "part-process";

interface ImportPreviewModalV1Props {
  open: boolean;
  data: ParsedImportV1Result | null;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

const TAB_LABELS: { key: PreviewTab; label: string }[] = [
  { key: "info", label: "Child Part Info" },
  { key: "material-cost", label: "Material Cost" },
  { key: "part-process", label: "Part Process" },
];

const errorCell = {
  backgroundColor: "#ffebee",
  color: "#d32f2f",
  fontWeight: 600,
};

const fmtNum = (v: number | string | null | undefined): number | string => {
  if (typeof v !== "number") return v ?? "-";
  return parseFloat(v.toPrecision(10));
};

const findWarning = (
  warnings: ImportWarning[],
  type: ImportWarning["type"],
  childPartNo?: string,
  processName?: string,
): ImportWarning | undefined =>
  warnings.find(
    (w) =>
      w.type === type &&
      (!childPartNo || w.childPartNo === childPartNo) &&
      (!processName || w.processName === processName),
  );

const WarningIcon = ({ warning }: { warning: ImportWarning }) => (
  <Tooltip title={warning.message} arrow placement="top">
    <Warning
      sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle", cursor: "help" }}
    />
  </Tooltip>
);

// ─── Header Info ──────────────────────────────────────────────────────────────

const HeaderInfo = ({
  data,
  warnings,
}: {
  data: ParsedImportV1Result;
  warnings: ImportWarning[];
}) => {
  const h = data.preview.header;
  const partNotFoundWarn = warnings.find((w) => w.type === "part-not-found");
  const fields = [
    { label: "Part No.", value: h.part_no },
    { label: "Part Name", value: h.part_name },
    { label: "AC", value: h.ac },
    { label: "RT", value: h.rt },
  ];
  return (
    <Box display="flex" gap={3} flexWrap="wrap" mt={1} mb={2}>
      {fields.map((f) => (
        <Box key={f.label}>
          <Typography variant="caption" color="textSecondary">
            {f.label}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={
              partNotFoundWarn && f.label === "Part No." ? errorCell : undefined
            }
          >
            {f.value || "-"}
            {partNotFoundWarn && f.label === "Part No." && (
              <WarningIcon warning={partNotFoundWarn} />
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Child Part Info Tab ──────────────────────────────────────────────────────

const InfoTab = ({
  parts,
  warnings,
}: {
  parts: ImportedChildPartV1[];
  warnings: ImportWarning[];
}) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {[
            "No.",
            "Name",
            "Qty",
            "Material Code",
            "Amount",
            "Ratio",
            "Consumption",
            "Part Weight",
            "Scrap Weight",
          ].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {parts.map((cp) => {
          const matWarn = findWarning(
            warnings,
            "material-not-found",
            cp.child_part_no,
          );
          return (
            <TableRow key={cp.child_part_no} hover>
              <TableCell>{cp.child_part_no}</TableCell>
              <TableCell>{cp.child_part_name}</TableCell>
              <TableCell>{fmtNum(cp.qty)}</TableCell>
              <TableCell sx={matWarn ? errorCell : undefined}>
                {cp.material_code || "-"}
                {matWarn && <WarningIcon warning={matWarn} />}
              </TableCell>
              <TableCell>{fmtNum(cp.amount)}</TableCell>
              <TableCell>{fmtNum(cp.ratio)}</TableCell>
              <TableCell>{fmtNum(cp.consumption)}</TableCell>
              <TableCell>{fmtNum(cp.part_weight)}</TableCell>
              <TableCell>{fmtNum(cp.scrap_weight)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Material Cost Tab ────────────────────────────────────────────────────────

const MaterialCostTab = ({
  parts,
  warnings,
}: {
  parts: ImportedChildPartV1[];
  warnings: ImportWarning[];
}) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {[
            "No.",
            "Is Purchased",
            "Material Cost",
            "Admin",
            "Profit",
            "2nd Tier Processes",
            "Other Processes",
            "Percent",
          ].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {parts.map((cp) => (
          <TableRow key={cp.child_part_no} hover>
            <TableCell>{cp.child_part_no}</TableCell>
            <TableCell>{cp.is_purchased ? "Yes" : "No"}</TableCell>
            <TableCell>{cp.material_cost !== null ? fmtNum(cp.material_cost) : "-"}</TableCell>
            <TableCell>{fmtNum(cp.admin)}</TableCell>
            <TableCell>{fmtNum(cp.profit)}</TableCell>
            <TableCell>
              {cp.second_tier_processes.map((p) => {
                const w = findWarning(
                  warnings,
                  "material-cost-process-not-found",
                  undefined,
                  p.name,
                );
                return (
                  <Box key={p.name} component="span" mr={0.5}>
                    <Chip
                      label={`${p.name}: ${fmtNum(p.cost)}`}
                      size="small"
                      sx={
                        w
                          ? { backgroundColor: "#ffebee", color: "#d32f2f" }
                          : undefined
                      }
                    />
                    {w && <WarningIcon warning={w} />}
                  </Box>
                );
              })}
            </TableCell>
            <TableCell>
              {cp.other_processes.map((p) => {
                const w = findWarning(
                  warnings,
                  "material-cost-process-not-found",
                  undefined,
                  p.name,
                );
                return (
                  <Box key={p.name} component="span" mr={0.5}>
                    <Chip
                      label={`${p.name}: ${fmtNum(p.cost)}`}
                      size="small"
                      sx={
                        w
                          ? { backgroundColor: "#ffebee", color: "#d32f2f" }
                          : undefined
                      }
                    />
                    {w && <WarningIcon warning={w} />}
                  </Box>
                );
              })}
            </TableCell>
            <TableCell>{fmtNum(cp.percent)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Part Process Tab ─────────────────────────────────────────────────────────

const PartProcessTab = ({
  parts,
  warnings,
}: {
  parts: ImportedChildPartV1[];
  warnings: ImportWarning[];
}) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {[
            "No.",
            "OEM Processes",
            "OEM Process Admin",
            "OEM Process Profit",
            "S/P Processes",
            "S/P Process Admin",
            "S/P Process Profit",
            "Admin OEM",
            "Profit OEM",
            "Admin S/P",
            "Profit S/P",
          ].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {parts.map((cp) => (
          <TableRow key={cp.child_part_no} hover>
            <TableCell>{cp.child_part_no}</TableCell>
            <TableCell>
              {cp.oem_processes.map((p) => {
                const w = findWarning(
                  warnings,
                  "material-cost-process-not-found",
                  undefined,
                  p.name,
                );
                return (
                  <Box key={p.name} component="span" mr={0.5}>
                    <Chip
                      label={`${p.name}: ${fmtNum(p.cost)}`}
                      size="small"
                      sx={
                        w
                          ? { backgroundColor: "#ffebee", color: "#d32f2f" }
                          : undefined
                      }
                    />
                    {w && <WarningIcon warning={w} />}
                  </Box>
                );
              })}
            </TableCell>
            <TableCell>{fmtNum(cp.oem_process_admin)}</TableCell>
            <TableCell>{fmtNum(cp.oem_process_profit)}</TableCell>
            <TableCell>
              {cp.spare_processes.map((p) => {
                const w = findWarning(
                  warnings,
                  "material-cost-process-not-found",
                  undefined,
                  p.name,
                );
                return (
                  <Box key={p.name} component="span" mr={0.5}>
                    <Chip
                      label={`${p.name}: ${fmtNum(p.cost)}`}
                      size="small"
                      sx={
                        w
                          ? { backgroundColor: "#ffebee", color: "#d32f2f" }
                          : undefined
                      }
                    />
                    {w && <WarningIcon warning={w} />}
                  </Box>
                );
              })}
            </TableCell>
            <TableCell>{fmtNum(cp.spare_process_admin)}</TableCell>
            <TableCell>{fmtNum(cp.spare_process_profit)}</TableCell>
            <TableCell>{fmtNum(cp.cp_oem_admin)}</TableCell>
            <TableCell>{fmtNum(cp.cp_oem_profit)}</TableCell>
            <TableCell>{fmtNum(cp.cp_spare_admin)}</TableCell>
            <TableCell>{fmtNum(cp.cp_spare_profit)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────

const ImportPreviewModalV1 = ({
  open,
  data,
  onClose,
  onConfirm,
  isSubmitting,
}: ImportPreviewModalV1Props) => {
  const [activeTab, setActiveTab] = useState<PreviewTab>("info");

  if (!data) return null;

  const { preview, warnings } = data;
  const warningCount = warnings.length;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90vw",
          maxWidth: 1100,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h2">Import Part V1 — Preview</Typography>
        <Divider sx={{ my: 1 }} />

        <HeaderInfo data={data} warnings={warnings} />

        {warningCount > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            พบ {warningCount} รายการที่มีปัญหา — ตรวจสอบช่องที่แสดงสีแดง
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, v: PreviewTab) => setActiveTab(v)}
          sx={{ mb: 1, borderBottom: 1, borderColor: "divider" }}
        >
          {TAB_LABELS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {activeTab === "info" && (
            <InfoTab parts={preview.child_parts} warnings={warnings} />
          )}
          {activeTab === "material-cost" && (
            <MaterialCostTab parts={preview.child_parts} warnings={warnings} />
          )}
          {activeTab === "part-process" && (
            <PartProcessTab parts={preview.child_parts} warnings={warnings} />
          )}
        </Box>

        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{ width: 120 }}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ ...DefaultPrimaryButton, width: 120 }}
            onClick={onConfirm}
            disabled={isSubmitting || warningCount > 0}
          >
            {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImportPreviewModalV1;
